import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Skeleton from 'react-loading-skeleton';
import { Icon } from 'assets/icons';
import { Tooltip } from 'app/shared/tooltip';
import { useCampaignService } from 'hooks/mailing/campaign';
import { useMailingReportService } from 'hooks/mailing/report';
import { formatDateWithoutDayOfWeek } from 'app/shared/utils/date';
import { truncateText } from 'app/shared/utils/general';
import styles from './campaign.module.css';

const getPerformanceBadge = (open_rate, avg_open_rate, ctor, avg_ctor) => {
  if (avg_open_rate === null || avg_open_rate === 0) return null;
  const openDiff = ((open_rate - avg_open_rate) / avg_open_rate) * 100;

  if (ctor !== null && ctor >= 80) return 'strong';

  if (ctor !== null && avg_ctor !== null && avg_ctor > 0) {
    const ctorDiff = ((ctor - avg_ctor) / avg_ctor) * 100;
    if (ctorDiff >= 50 && openDiff >= -10) return 'strong';
    if (ctorDiff < -50) {
      return openDiff >= -10 ? 'average' : openDiff >= -20 ? 'below_avg' : 'poor';
    }
  }

  if (openDiff >= 10) return 'strong';
  if (openDiff >= -10) return 'average';
  if (openDiff >= -20) return 'below_avg';
  return 'poor';
};

const formatCTOR = (ctor) => {
  if (ctor === null || ctor === undefined) return '—';
  return `${Math.round(ctor)}%`;
};

const BADGE_STYLES = {
  strong: styles.badgeStrong,
  average: styles.badgeAverage,
  below_avg: styles.badgeBelowAvg,
  poor: styles.badgePoor
};

const BADGE_LABELS = {
  strong: 'strong',
  average: 'average',
  below_avg: 'below avg',
  poor: 'poor'
};

const PerformanceBadge = ({ badge }) => {
  if (!badge) return <span className={styles.dash}>—</span>;
  return <span className={`${styles.badge} ${BADGE_STYLES[badge]}`}>{BADGE_LABELS[badge]}</span>;
};

const SkeletonRows = () => (
  <>
    {Array.from({ length: 8 }).map((_, i) => (
      <tr key={i} className={styles.row}>
        <td className={styles.cellCampaign}>
          <Skeleton width={140} height={13} />
          <Skeleton width={80} height={11} style={{ marginTop: 4 }} />
        </td>
        <td className={styles.cellNum}>
          <Skeleton width={40} height={13} />
        </td>
        <td className={styles.cellNum}>
          <Skeleton width={32} height={13} />
        </td>
        <td className={styles.cellNum}>
          <Skeleton width={32} height={13} />
        </td>
        <td className={styles.cellBadge}>
          <Skeleton width={52} height={20} borderRadius={4} />
        </td>
      </tr>
    ))}
    <tr className={styles.avgRow}>
      <td className={styles.cellCampaign}>
        <Skeleton width={100} height={13} />
      </td>
      <td className={styles.cellNum}>
        <Skeleton width={40} height={13} />
      </td>
      <td className={styles.cellNum}>
        <Skeleton width={32} height={13} />
      </td>
      <td className={styles.cellNum}>
        <Skeleton width={32} height={13} />
      </td>
      <td className={styles.cellBadge} />
    </tr>
  </>
);

const EmptyState = () => (
  <tr>
    <td colSpan={5}>
      <div className={styles.empty}>
        <Icon name="campaign" width={22} height={22} className={styles.emptyIcon} />
        <p className={styles.emptyTitle}>No campaigns sent yet</p>
        <p className={styles.emptyBody}>
          Your campaign performance will appear here after your first send.
        </p>
      </div>
    </td>
  </tr>
);

export const CampaignActivity = () => {
  const navigate = useNavigate();
  const { fetchCampaigns } = useCampaignService();
  const { fetchReports } = useMailingReportService();

  const [campaigns, setCampaigns] = useState([]);
  const [averages, setAverages] = useState({ avg_sent: 0, avg_open_rate: null, avg_ctor: null });
  const [total_count, setTotalCount] = useState(0);
  const [history_count, setHistoryCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);

    const threeMonthsAgo = new Date();
    threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
    const since = Date.parse(threeMonthsAgo.toISOString().split('T')[0]);

    try {
      const { campaigns: history = [], size = 0 } = await fetchCampaigns({
        query_string: `page=0&population=100&bool=-is_test_campaign&status=processed&sort_by=-schedule.date&return_only=id,name,schedule.date&time_stamp=${since}~`
      });

      setTotalCount(size);
      setHistoryCount(history.length);

      if (history.length === 0) {
        setCampaigns([]);
        return;
      }

      const { logs: reports = [] } = await fetchReports({
        query_string: `resource_type=campaign&resource_id=${history
          .map((c) => c.id)
          .join(',')}&return_only=resource_id,stats`
      });

      const statsByCampaignId = reports.reduce((acc, { resource_id, stats = {} }) => {
        const prev = acc[resource_id] || { sent: 0, opened: 0, clicked_link: 0 };
        acc[resource_id] = {
          sent: prev.sent + (stats.sent || 0),
          opened: prev.opened + (stats.opened || 0),
          clicked_link: prev.clicked_link + (stats.clicked_link || 0)
        };
        return acc;
      }, {});

      const toStats = (id) => {
        const s = statsByCampaignId[id] || { sent: 0, opened: 0, clicked_link: 0 };
        const open_rate = s.sent > 0 ? (s.opened / s.sent) * 100 : 0;
        const ctor = s.opened > 0 ? (s.clicked_link / s.opened) * 100 : null;
        return { sent_count: s.sent, open_rate, ctor };
      };

      const enriched = history.map((campaign) => ({ ...campaign, ...toStats(campaign.id) }));
      setCampaigns(enriched.slice(0, 5));

      const totals = history.reduce(
        (acc, c) => {
          const s = statsByCampaignId[c.id] || { sent: 0, opened: 0, clicked_link: 0 };
          return {
            sent: acc.sent + s.sent,
            opened: acc.opened + s.opened,
            clicked_link: acc.clicked_link + s.clicked_link
          };
        },
        { sent: 0, opened: 0, clicked_link: 0 }
      );
      setAverages({
        avg_sent: history.length > 0 ? totals.sent / history.length : 0,
        avg_open_rate: totals.sent > 0 ? (totals.opened / totals.sent) * 100 : null,
        avg_ctor: totals.opened > 0 ? (totals.clicked_link / totals.opened) * 100 : null
      });
    } catch {
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();

    const handleFocus = () => {
      if (!document.hidden) load();
    };
    document.addEventListener('visibilitychange', handleFocus);
    return () => document.removeEventListener('visibilitychange', handleFocus);
  }, [load]);

  const show_view_all = total_count > 8;
  const has_min_history = history_count >= 3 && averages.avg_open_rate !== null;

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <span className={styles.title}>Recent Campaigns</span>
        {show_view_all && (
          <button className={styles.viewAll} onClick={() => navigate('/campaigns')}>
            View all
          </button>
        )}
      </div>

      <table className={styles.table}>
        <thead>
          <tr className={styles.headRow}>
            <th className={styles.headCampaign}>Campaign</th>
            <th className={styles.headNum}>Sent</th>
            <th className={styles.headNum}>Opens</th>
            <th className={styles.headNum}>
              CTOR
              <Tooltip content="Click-to-open rate — the % of openers who clicked a link">
                <span className={styles.infoTip}>
                  <Icon name="info" width={13} height={13} />
                </span>
              </Tooltip>
            </th>
            <th className={styles.headBadge}>
              Performance
              <Tooltip content="Relative to your account metrics over the last 3 months">
                <span className={styles.infoTip}>
                  <Icon name="info" width={13} height={13} />
                </span>
              </Tooltip>
            </th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <SkeletonRows />
          ) : campaigns.length === 0 ? (
            <EmptyState />
          ) : (
            <>
              {campaigns.map((campaign) => {
                const badge = has_min_history
                  ? getPerformanceBadge(
                      campaign.open_rate,
                      averages.avg_open_rate,
                      campaign.ctor,
                      averages.avg_ctor
                    )
                  : null;
                return (
                  <tr
                    key={campaign.id}
                    className={styles.row}
                    onClick={() => navigate(`/campaigns/${campaign.id}`)}
                  >
                    <td className={styles.cellCampaign}>
                      <span className={styles.campaignName} title={campaign.name}>
                        {truncateText(campaign.name, 35)}
                      </span>
                      <span className={styles.campaignDate}>
                        {formatDateWithoutDayOfWeek(campaign.schedule?.date)}
                      </span>
                    </td>
                    <td className={styles.cellNum}>
                      {(campaign.sent_count || 0).toLocaleString()}
                    </td>
                    <td className={styles.cellNum}>{Math.round(campaign.open_rate || 0)}%</td>
                    <td className={styles.cellNum}>{formatCTOR(campaign.ctor)}</td>
                    <td className={styles.cellBadge}>
                      <PerformanceBadge badge={badge} />
                    </td>
                  </tr>
                );
              })}
              <tr className={styles.avgRow}>
                <td className={styles.cellCampaign}>
                  <span className={styles.avgLabel}>Account average</span>
                </td>
                <td className={styles.cellNum}>
                  {averages.avg_sent > 0 ? Math.round(averages.avg_sent).toLocaleString() : '—'}
                </td>
                <td className={styles.cellNum}>
                  {averages.avg_open_rate > 0 ? `${Math.round(averages.avg_open_rate)}%` : '—'}
                </td>
                <td className={styles.cellNum}>
                  {averages.avg_ctor > 0 ? `${Math.round(averages.avg_ctor)}%` : '—'}
                </td>
                <td className={styles.cellBadge} />
              </tr>
            </>
          )}
        </tbody>
      </table>
    </div>
  );
};
